import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { CompanyDailyNavConfigComp, CompanyDailyNavConfigVersion } from 'api';
import { firmDailyNavSettingsUrl } from 'common/config/urls';
import {
  buildDailyNavCompanyConfigDiff,
  DAILY_NAV_APPLY_BUTTON_LABEL,
  DAILY_NAV_THRESHOLD_KEYS,
  dailyNavApplyDatesLabel,
  dailyNavApplyDisabledReason,
  dailyNavApplyRefusalMessage,
  dailyNavDateLabel,
  dailyNavNextDayLabel,
  DailyNavThresholdKey,
  fractionToPercent,
  getDailyNavThresholdGroups,
  isValidThresholdPercent,
  percentToFraction,
  selectDailyNavApplyDates,
  selectDailyNavGoverningVersions,
} from 'common/constants/dailyNav';
import { useStore } from 'common/store';
import { CurrentCompanyMeasurementDate, SelectedMeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import AlexandriaCompanyPicker from 'components/AlexandriaCompanyPicker';
import DailyNavConfigDiff from 'components/DailyNavConfigDiff';
import { GridSkeleton } from 'components/Grid';
import AddGpcDialog from 'pages/Valuations/approaches/guidelinePublicCompanies/AddGpcDialog';
import { CapIQ, CapIQCompanyData } from 'pages/ValuationsAllocation/types';
import { useReadUserAccessLevel } from 'services/hooks';
import {
  useApplyDailyNavConfig,
  useGetCompanyDailyNavConfig,
  useGetCompanyDailyNavConfigHistory,
  useGetCompanyGpcApproaches,
  useGetDailyNavConfigApplyPreview,
  useUpdateCompanyDailyNavConfig,
} from 'services/hooks/dailyNav';
import { ADD_COMP_OR_ETF_BUTTON_LABEL, ADD_PUBLIC_OR_ETF_DIALOG_TITLE } from './constants';
import { DAILY_MONITORING_TEST_IDS } from './DailyMonitoring.testIds';

// Overrides are kept as strings: '' means "inherited from the firm default",
// while any other value — including '0' — is an explicit override.
type OverridesState = Record<DailyNavThresholdKey, string>;

const EMPTY_OVERRIDES = Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, ''])) as OverridesState;

const formatEffectivePeriod = (version: { effective_from: string; effective_to?: string | null }) => {
  const from = format(new Date(version.effective_from), 'MMM d, yyyy h:mm a');
  const to = version.effective_to ? format(new Date(version.effective_to), 'MMM d, yyyy h:mm a') : 'Present';
  return `${from} — ${to}`;
};

const CHANGE_TRIGGER_LABELS: Record<CompanyDailyNavConfigVersion.change_trigger, string> = {
  [CompanyDailyNavConfigVersion.change_trigger.FIRM_TEMPLATE_CHANGE]: 'Firm template change',
  [CompanyDailyNavConfigVersion.change_trigger.COMPANY_EDIT]: 'Company edit',
};

// Draft shape for the comps watchlist. The API's readonly CompanyDailyNavConfigComp adds
// an `order` field we don't need locally; the write payload matches this shape exactly.
type CompRow = {
  comp_type: CompanyDailyNavConfigComp.comp_type;
  cap_iq_id: string;
  ticker_symbol: string;
  company_name: string;
};

const COMP_TYPE_LABELS: Record<CompanyDailyNavConfigComp.comp_type, string> = {
  [CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP]: 'Public Comp',
  [CompanyDailyNavConfigComp.comp_type.ETF]: 'ETF',
};

const toCompRow = (comp: {
  comp_type: CompanyDailyNavConfigComp.comp_type;
  cap_iq_id: string;
  ticker_symbol: string;
  company_name: string;
}): CompRow => ({
  comp_type: comp.comp_type,
  cap_iq_id: comp.cap_iq_id,
  ticker_symbol: comp.ticker_symbol,
  company_name: comp.company_name,
});

// Cap IQ fields come wrapped in a { value } envelope; extract to a plain string.
const capIqFieldToString = (field?: CapIQ): string =>
  field?.value === null || field?.value === undefined ? '' : String(field.value);

// ticker_symbol values from Cap IQ come as "Exchange:Symbol" (e.g. "NASDAQ:AAPL");
// strip the exchange prefix so we store just the symbol.
const stripExchangePrefix = (ticker: string): string =>
  ticker.includes(':') ? (ticker.split(':').pop() ?? ticker) : ticker;

// Cap IQ classifies ETFs and index funds as "public fund" / "index"; everything
// else searched via the comparable-company picker is treated as a public comp.
const ETF_COMPANY_TYPES = ['public fund', 'index'];
const inferCompType = (companyType: string): CompanyDailyNavConfigComp.comp_type =>
  ETF_COMPANY_TYPES.includes(companyType.trim().toLowerCase())
    ? CompanyDailyNavConfigComp.comp_type.ETF
    : CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP;

// Reported up to CompanySummary so the Save action can be rendered in the tertiary
// nav instead of inline. CompanySummary owns the single tertiary-nav registration
// slot, so DailyMonitoring must never register its own tertiary nav state.
export interface DailyMonitoringSaveAction {
  disabled: boolean;
  isSaving: boolean;
  isVisible: boolean;
  onSave: () => void;
}

type DailyMonitoringProps = {
  // The measurement date selected via useCompanyMeasurementDateFilter (threaded
  // from CompanySummary). Its cmd_id scopes the GPC-approaches lookup; its id
  // (the MeasurementDate pk) is what the Cap IQ pc-list endpoint expects.
  selectedMeasurementDate?: SelectedMeasurementDate;
  // Reports the current Save action (or null when it should be hidden) up to
  // CompanySummary, which renders it via the tertiary nav registration it owns.
  onSaveActionChange?: (action: DailyMonitoringSaveAction | null) => void;
};

const DailyMonitoring = ({
  selectedMeasurementDate,
  onSaveActionChange,
}: DailyMonitoringProps) => {
  const [{ companyInfo, firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();

  const firmId = firmInfo?.id ?? null;
  const companyId = companyInfo?.id ?? null;

  const { isAdminOrStaffOrSuperuser } = useReadUserAccessLevel({ user, firmInfo });

  const { data: currentConfig, isLoading } = useGetCompanyDailyNavConfig(firmId, companyId);
  const { history, loadMoreHistory, hasMoreHistory, isLoadingMoreHistory } = useGetCompanyDailyNavConfigHistory(
    firmId,
    companyId
  );
  const { updateCompanyDailyNavConfig, isPending } = useUpdateCompanyDailyNavConfig();
  const { approaches: gpcApproaches } = useGetCompanyGpcApproaches(selectedMeasurementDate?.cmd_id);
  const {
    data: applyPreview,
    isLoading: isApplyPreviewLoading,
    refetch: refetchApplyPreview,
  } = useGetDailyNavConfigApplyPreview(firmId, companyId);
  const { applyDailyNavConfig, isPending: isApplyPending } = useApplyDailyNavConfig();

  const [overrides, setOverrides] = useState<OverridesState>(EMPTY_OVERRIDES);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [comps, setComps] = useState<CompRow[]>([]);
  const [selectedGpcApproachId, setSelectedGpcApproachId] = useState<number | ''>('');
  const [alexandriaId, setAlexandriaId] = useState<string | null>(null);
  const [monitoringEnabled, setMonitoringEnabled] = useState(true);
  // Null = closed; mirrors DailyNavSettings. `versionId` is the FIRM version the
  // clicked company version is anchored to, backing the concurrency check.
  const [applyDialog, setApplyDialog] = useState<{ versionId: number | null; fromSave: boolean } | null>(null);
  const applyFirmVersionId = applyDialog?.versionId ?? null;

  // Company-scoped preview. `dates` also carries the days that CAN'T be applied, so the
  // applicable ones have to be filtered out rather than read off the front -- see
  // selectDailyNavApplyDates. A blocked_reason usually means the firm template changed
  // today: all of a date's records must share one firm version.
  const { hasDates, applicableDates, blockedDate, recordCount } = useMemo(
    () => selectDailyNavApplyDates(applyPreview?.dates),
    [applyPreview?.dates]
  );

  const { anchoredCompanyVersionId, navDate: governingNavDate, nextNavDate } = useMemo(
    () => selectDailyNavGoverningVersions(applyPreview),
    [applyPreview]
  );
  // From the backend's count of records that WOULD move, not from comparing ids: a firm
  // save cascades a new company version to every company, so ids differ far more often
  // than this company's rules actually changed.
  const hasDivergence = applicableDates.length > 0 && recordCount > 0;

  const isApplyPreviewDateApplicable = applicableDates.length > 0;
  // An applicable date is not enough: in the steady state this company's record is
  // already on its open version, so applying would move nothing.
  const hasNothingToApply = isApplyPreviewDateApplicable && recordCount === 0;
  const canApplyNow = isApplyPreviewDateApplicable && recordCount > 0;
  // Avoids flashing the empty-state hint before the preview has loaded.
  const hasNoOpenNavDay = !isApplyPreviewLoading && !hasDates;

  // Why an apply is not on offer, for the acknowledgement form of the dialog.
  const applyUnavailableReason = useMemo(
    () => dailyNavApplyRefusalMessage(applyPreview?.dates),
    [applyPreview?.dates]
  );

  // What the save changed: the company version just opened against the one it replaced.
  // History is ordered -effective_from, so the entry AFTER a version is its predecessor.
  // Matched on the company version, not applyDialog's FIRM versionId -- different id
  // spaces. `is_active` finds it because a save always opens a new version.
  const companyDialogDiff = useMemo(() => {
    const index = history?.findIndex(version => version.is_active) ?? -1;
    return index < 0 ? [] : buildDailyNavCompanyConfigDiff(history?.[index + 1], history?.[index]);
  }, [history]);

  // A decision when a day is open, an acknowledgement otherwise.
  const applyDialogTitle = useMemo(() => {
    if (!canApplyNow) {
      return 'Daily NAV configuration saved';
    }
    return applyDialog?.fromSave
      ? 'Saved — apply to this company’s open NAV day?'
      : 'Apply these settings to this company’s open NAV day?';
  }, [canApplyNow, applyDialog?.fromSave]);

  const firmConfig = currentConfig?.firm_config ?? null;
  const companyConfig = currentConfig?.company_config ?? null;
  // Company overrides anchor to the firm's open config version; until the firm
  // has saved a template at least once, there is nothing to anchor to.
  const hasSavedFirmConfig = Boolean(firmConfig?.id);
  const canEdit = isAdminOrStaffOrSuperuser && hasSavedFirmConfig;

  // Windows are firm-level and inherited here, so the counts come from the
  // anchoring firm config rather than anything this form edits.
  const inheritedThresholdGroups = useMemo(
    () => getDailyNavThresholdGroups(firmConfig?.short_window_sessions, firmConfig?.long_window_sessions),
    [firmConfig?.short_window_sessions, firmConfig?.long_window_sessions]
  );

  useEffect(() => {
    if (currentConfig) {
      const companyValues = currentConfig.company_config;
      setOverrides(
        Object.fromEntries(
          // Null from the API means "inherited"; 0 is a valid explicit override
          DAILY_NAV_THRESHOLD_KEYS.map(key => [key, fractionToPercent(companyValues?.[key])])
        ) as OverridesState
      );
      setComps((companyValues?.comps ?? []).map(toCompRow));
      setAlexandriaId(companyValues?.alexandria_id ?? null);
      setMonitoringEnabled(companyValues?.monitoring_enabled ?? true);
    }
  }, [currentConfig]);

  const isFormValid = useMemo(
    () => DAILY_NAV_THRESHOLD_KEYS.every(key => overrides[key] === '' || isValidThresholdPercent(overrides[key])),
    [overrides]
  );

  const isCompsDirty = useMemo(() => {
    const savedComps = companyConfig?.comps ?? [];
    if (savedComps.length !== comps.length) {
      return true;
    }
    return savedComps.some((savedComp, index) => {
      const draftComp = comps[index];
      return (
        !draftComp ||
        draftComp.comp_type !== savedComp.comp_type ||
        draftComp.cap_iq_id !== savedComp.cap_iq_id ||
        draftComp.ticker_symbol !== savedComp.ticker_symbol ||
        draftComp.company_name !== savedComp.company_name
      );
    });
  }, [companyConfig, comps]);

  const isAlexandriaDirty = (companyConfig?.alexandria_id ?? null) !== alexandriaId;
  const isMonitoringDirty = (companyConfig?.monitoring_enabled ?? true) !== monitoringEnabled;

  const isDirty = useMemo(
    () =>
      DAILY_NAV_THRESHOLD_KEYS.some(key => fractionToPercent(companyConfig?.[key]) !== overrides[key]) ||
      isCompsDirty ||
      isAlexandriaDirty ||
      isMonitoringDirty,
    [companyConfig, overrides, isCompsDirty, isAlexandriaDirty, isMonitoringDirty]
  );

  const saveDailyMonitoringConfig = useCallback(async () => {
    if (!firmId || !companyId || !isFormValid || !hasSavedFirmConfig) {
      return;
    }
    const toPayloadValue = (key: DailyNavThresholdKey) =>
      overrides[key] === '' ? null : percentToFraction(overrides[key]);
    const updatedConfig = await updateCompanyDailyNavConfig(firmId, companyId, {
      cap_iq_short_window_delta: toPayloadValue('cap_iq_short_window_delta'),
      cap_iq_long_window_delta: toPayloadValue('cap_iq_long_window_delta'),
      cap_iq_since_last_valuation_delta: toPayloadValue('cap_iq_since_last_valuation_delta'),
      secondary_short_window_delta: toPayloadValue('secondary_short_window_delta'),
      secondary_long_window_delta: toPayloadValue('secondary_long_window_delta'),
      secondary_since_last_valuation_delta: toPayloadValue('secondary_since_last_valuation_delta'),
      secondary_change_from_mark_delta: toPayloadValue('secondary_change_from_mark_delta'),
      comps,
      alexandria_id: alexandriaId,
      monitoring_enabled: monitoringEnabled,
    });
    if (!updatedConfig) {
      return;
    }
    enqueueSnackbar('Daily NAV Settings configuration successfully saved', { variant: 'success' });

    // Opened unconditionally, matching the firm settings page; refetched first so the
    // decision is made against the version this save just opened.
    //
    // The FIRM version id, not updatedConfig.id: company versions are a different id
    // space and would collide with unrelated firm rows.
    await refetchApplyPreview();
    setApplyDialog({ versionId: updatedConfig.firm_config_version?.id ?? null, fromSave: true });
  }, [
    firmId,
    companyId,
    isFormValid,
    hasSavedFirmConfig,
    overrides,
    comps,
    alexandriaId,
    monitoringEnabled,
    updateCompanyDailyNavConfig,
    refetchApplyPreview,
    enqueueSnackbar,
  ]);

  // saveDailyMonitoringConfig's identity changes whenever overrides/comps change; wrap it in a
  // stable callback (backed by a ref) so registering it with onSaveActionChange doesn't churn.
  const saveDailyMonitoringConfigRef = useRef(saveDailyMonitoringConfig);
  useEffect(() => {
    saveDailyMonitoringConfigRef.current = saveDailyMonitoringConfig;
  }, [saveDailyMonitoringConfig]);
  const stableSave = useCallback(() => saveDailyMonitoringConfigRef.current(), []);

  const saveActionDisabled = !firmId || !companyId || !isFormValid || !isDirty || !hasSavedFirmConfig || isPending;

  // Report the Save action up to CompanySummary, which owns the single tertiary-nav
  // registration slot. Do not register tertiary nav state directly from this component.
  useEffect(() => {
    onSaveActionChange?.({
      disabled: saveActionDisabled,
      isSaving: isPending,
      isVisible: isAdminOrStaffOrSuperuser,
      onSave: stableSave,
    });
    return () => onSaveActionChange?.(null);
  }, [saveActionDisabled, isPending, isAdminOrStaffOrSuperuser, stableSave, onSaveActionChange]);

  const handleConfirmApply = useCallback(async () => {
    if (!firmId || !companyId) {
      return;
    }
    if (applyFirmVersionId === null) {
      return;
    }
    setApplyDialog(null);
    const response = await applyDailyNavConfig(firmId, applyFirmVersionId, companyId);
    if (!response) {
      return;
    }
    if (!response.reanchored_count) {
      // A refusal and an idempotent no-op both come back 200 with 0 moved; only the
      // response's own blocked_reason separates them.
      const refusal = dailyNavApplyRefusalMessage(response.dates);
      enqueueSnackbar(refusal || 'Nothing needed applying — this record already reflects the open config', {
        variant: refusal ? 'warning' : 'info',
      });
      return;
    }
    enqueueSnackbar('Re-pointed this company’s record onto the open config', { variant: 'success' });
    // applyFirmVersionId must stay in the deps: it is read here, so omitting it pins this
    // callback to the render that created it -- where the dialog was still closed and the
    // id null, making the confirm button a silent no-op.
  }, [firmId, companyId, applyFirmVersionId, applyDailyNavConfig, enqueueSnackbar]);

  // isDirty included: unsaved overrides/comps/Alexandria edits aren't what gets applied.
  const applyActionDisabled = !firmId || !companyId || isApplyPending || isDirty || !canApplyNow;
  const applyDisabledReason = dailyNavApplyDisabledReason({
    hasNothingToApply,
    isDisabled: applyActionDisabled,
    isDirty,
    hasNoOpenNavDay,
    blockedDate,
  });

  const selectedVersion = useMemo(
    () => history?.find(version => version.id === selectedVersionId) ?? null,
    [history, selectedVersionId]
  );

  // Company Summary isn't wrapped in ValuationContext, so AddGpcDialog can't
  // read the page's date from context and would otherwise fall back to the
  // firm's oldest measurement date. Build the same CompanyMeasurementDate shape
  // ValuationsAllocation derives from useCompanyMeasurementDateFilter: `id` is
  // the CompanyMeasurementDate pk and `md_id` the MeasurementDate pk (the filter
  // exposes those as cmd_id and id respectively). Paired with currentMarketDate
  // below, the Cap IQ search resolves against the selected date rather than the
  // oldest firm date.
  const gpcDialogMeasurementDate = useMemo<CurrentCompanyMeasurementDate | undefined>(() => {
    if (!selectedMeasurementDate) {
      return undefined;
    }
    return {
      id: selectedMeasurementDate.cmd_id ?? null,
      name: selectedMeasurementDate.name,
      slug: selectedMeasurementDate.slug ?? '',
      date: selectedMeasurementDate.date,
      is_open: selectedMeasurementDate.is_open ?? false,
      md_id: selectedMeasurementDate.id ?? null,
      company_id: selectedMeasurementDate.company_id ?? companyId ?? 0,
      company_name: companyInfo?.name ?? '',
      is_ready: selectedMeasurementDate.is_ready ?? false,
    };
  }, [selectedMeasurementDate, companyId, companyInfo?.name]);

  const handleOverrideChange = (key: DailyNavThresholdKey, value: string) => {
    setOverrides(prevState => ({ ...prevState, [key]: value }));
  };

  const handleResetToFirmDefault = (key: DailyNavThresholdKey) => {
    setOverrides(prevState => ({ ...prevState, [key]: '' }));
  };

  // Replaces the current comps list wholesale. This is a one-time copy — there's no
  // live link back to the GPC approach, so later GPC changes won't reflect here.
  const handleCopyFromGpcApproach = () => {
    const approach = gpcApproaches?.find(candidate => candidate.gpc_approach_id === selectedGpcApproachId);
    if (!approach) {
      return;
    }
    const copiedComps: CompRow[] = approach.comps
      .filter(comp => Boolean(comp.cap_iq_id))
      .map(comp => ({
        comp_type: CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP,
        cap_iq_id: comp.cap_iq_id as string,
        ticker_symbol: comp.ticker_symbol ?? '',
        company_name: comp.company_name ?? '',
      }));
    setComps(copiedComps);
  };

  // AddGpcDialog is a pure picker: it searches Cap IQ (read-only) and hands back the
  // selected comparable companies via onAdd. We snapshot each one's identity into the
  // local watchlist, inferring ETF vs public comp from its Cap IQ company_type, and
  // skip any already present (by cap_iq_id).
  const handleAddCompsFromDialog = (data: CapIQCompanyData[]) => {
    setComps(prevState => {
      const seenIds = new Set(prevState.map(comp => comp.cap_iq_id));
      const additions: CompRow[] = [];
      data.forEach(item => {
        const capIqId = capIqFieldToString(item.gpc?.cap_iq_id);
        if (!capIqId || seenIds.has(capIqId)) {
          return;
        }
        seenIds.add(capIqId);
        additions.push({
          comp_type: inferCompType(capIqFieldToString(item.gpc?.company_type)),
          cap_iq_id: capIqId,
          ticker_symbol: stripExchangePrefix(capIqFieldToString(item.gpc?.ticker_symbol)),
          company_name: capIqFieldToString(item.gpc?.company_name),
        });
      });
      return [...prevState, ...additions];
    });
  };

  const handleRemoveComp = (capIqId: string) => {
    setComps(prevState => prevState.filter(comp => comp.cap_iq_id !== capIqId));
  };

  if (isLoading) {
    return <GridSkeleton />;
  }

  return (
    <Grid container spacing={4} sx={{ mb: 10, flexDirection: { xs: 'column', lg: 'row' } }}>
      <Grid size={{ xs: 12, lg: 7 }}>
        <Typography variant="h6" gutterBottom>
          Daily NAV Settings
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Alert thresholds for this company. Fields left as inherited use the firm default; setting a value (including
          0%) overrides the firm default for this company only.
        </Typography>
        <FormControlLabel
          sx={{ mb: 3, display: 'flex' }}
          control={
            <Checkbox
              checked={monitoringEnabled}
              disabled={!canEdit}
              onChange={event => setMonitoringEnabled(event.target.checked)}
              id="daily-monitoring-enabled"
            />
          }
          label="Include this company in Daily NAV generation"
        />
        {!hasSavedFirmConfig && (
          <Alert severity="info" sx={{ mb: 3 }} data-testid={DAILY_MONITORING_TEST_IDS.noFirmConfigAlert}>
            This firm has not saved a Daily NAV template yet. Company-level overrides can&apos;t be configured until the
            firm saves its Daily NAV Settings.
            {isAdminOrStaffOrSuperuser && firmInfo?.slug && (
              <>
                {' '}
                <Link component={RouterLink} to={firmDailyNavSettingsUrl(firmInfo.slug)}>
                  Go to Daily NAV Settings
                </Link>
                .
              </>
            )}
          </Alert>
        )}
        {hasSavedFirmConfig && !isDirty && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 3, display: 'block' }}
            data-testid={DAILY_MONITORING_TEST_IDS.noChangesHint}>
            No changes to save.
          </Typography>
        )}
        {inheritedThresholdGroups.map(group => (
          <Box key={group.title} sx={{ mb: 3 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              {group.title.toUpperCase()}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {group.fields.map(field => {
                const isOverridden = overrides[field.key] !== '';
                const firmDefault = fractionToPercent(firmConfig?.[field.key]);

                return (
                  <Grid key={field.key} size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      id={`daily-monitoring-threshold-${field.key}`}
                      label={field.label}
                      value={overrides[field.key]}
                      placeholder={firmDefault}
                      onChange={e => handleOverrideChange(field.key, e.target.value)}
                      error={isOverridden && !isValidThresholdPercent(overrides[field.key])}
                      disabled={!canEdit}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              %
                              {isOverridden && (
                                <Tooltip title="Reset to firm default">
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    data-testid={DAILY_MONITORING_TEST_IDS.resetToFirmDefaultButton(field.key)}
                                    onClick={() => handleResetToFirmDefault(field.key)}
                                    disabled={!canEdit}>
                                    <RestartAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </InputAdornment>
                          ),
                        },
                        inputLabel: { shrink: true },
                      }}
                    />
                    <Chip
                      size="small"
                      sx={{ mt: 0.5 }}
                      data-testid={DAILY_MONITORING_TEST_IDS.thresholdSourceChip(field.key)}
                      label={isOverridden ? 'Override' : `Inherited (${firmDefault || '—'}%)`}
                      color={isOverridden ? 'primary' : 'default'}
                      variant={isOverridden ? 'filled' : 'outlined'}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ mb: 3 }} data-testid={DAILY_MONITORING_TEST_IDS.alexandriaSection}>
          <Typography variant="h6" gutterBottom>
            External Company Profile
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The external company profile this company&apos;s market data is pulled from. Saving a change
            opens a new config version, so records already evaluated keep the profile they used.
          </Typography>
          <AlexandriaCompanyPicker
            firmId={firmId}
            companyId={companyId}
            selectedAlexandriaId={alexandriaId}
            onChange={setAlexandriaId}
            disabled={!canEdit}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ mb: 3 }} data-testid={DAILY_MONITORING_TEST_IDS.compsSection}>
          <Typography variant="h6" gutterBottom>
            Comps Watchlist
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Public comps and ETFs tracked alongside this company&apos;s Daily NAV monitoring.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <Select
              size="small"
              displayEmpty
              value={selectedGpcApproachId}
              onChange={e => setSelectedGpcApproachId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={!canEdit || !gpcApproaches?.length}
              data-testid={DAILY_MONITORING_TEST_IDS.gpcApproachSelect}
              sx={{ minWidth: 260 }}>
              <MenuItem value="">
                <em>{gpcApproaches?.length ? 'Select a GPC approach' : 'No GPC approaches available'}</em>
              </MenuItem>
              {gpcApproaches?.map((approach, index) => (
                <MenuItem key={approach.gpc_approach_id} value={approach.gpc_approach_id}>
                  GPC Approach {index + 1} ({approach.comps.length} comp{approach.comps.length === 1 ? '' : 's'})
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="outlined"
              size="small"
              data-testid={DAILY_MONITORING_TEST_IDS.copyFromGpcButton}
              onClick={handleCopyFromGpcApproach}
              disabled={!canEdit || selectedGpcApproachId === ''}>
              Copy Comps
            </Button>
          </Stack>
          <Box sx={{ mb: 2 }}>
            <AddGpcDialog
              buttonLabel={ADD_COMP_OR_ETF_BUTTON_LABEL}
              currentComps={comps.map(comp => ({
                cap_iq_id: comp.cap_iq_id,
                ticker_symbol: comp.ticker_symbol,
                company_name: comp.company_name,
              }))}
              dialogTitle={ADD_PUBLIC_OR_ETF_DIALOG_TITLE}
              onAdd={handleAddCompsFromDialog}
              openGpcDialog={false}
              disabled={!canEdit}
              firmId={firmId ?? undefined}
              currentMeasurementDate={gpcDialogMeasurementDate}
              currentMarketDate={selectedMeasurementDate?.date}
            />
          </Box>
          {comps.length ? (
            <Table size="small" data-testid={DAILY_MONITORING_TEST_IDS.compsTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Type</TableCell>
                  {canEdit && <TableCell align="right">Remove</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {comps.map(comp => (
                  <TableRow key={comp.cap_iq_id}>
                    <TableCell>{comp.ticker_symbol}</TableCell>
                    <TableCell>{comp.company_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={COMP_TYPE_LABELS[comp.comp_type]}
                        color={comp.comp_type === CompanyDailyNavConfigComp.comp_type.ETF ? 'secondary' : 'primary'}
                        variant="outlined"
                        data-testid={DAILY_MONITORING_TEST_IDS.compTypeChip(comp.cap_iq_id)}
                      />
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveComp(comp.cap_iq_id)}
                            data-testid={DAILY_MONITORING_TEST_IDS.removeCompButton(comp.cap_iq_id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid={DAILY_MONITORING_TEST_IDS.noCompsHint}>
              No comps added yet.
            </Typography>
          )}
        </Box>
      </Grid>
      <Grid size={{ xs: 12, lg: 5 }}>
        <Paper elevation={4} sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Version History
          </Typography>
          {history?.length ? (
            <List dense>
              {history.map(version => (
                /* secondaryAction keeps the Apply button out of ListItemButton so the trailing
                     action isn't nested inside the row's own interactive element. */
                <ListItem
                  key={version.id}
                  disablePadding
                  secondaryAction={
                    version.is_active && isAdminOrStaffOrSuperuser ? (
                    // <span> required: a disabled MUI Button fires no hover events, so
                    // Tooltip needs it to render at all. aria-label carries the same reason,
                    // since a disabled button isn't focusable.
                      <Tooltip title={applyDisabledReason} describeChild>
                        <span
                          data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryApplyTooltip}
                          aria-label={applyDisabledReason || undefined}>
                          <Button
                            size="small"
                            variant="outlined"
                            data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryApplyButton}
                            onClick={event => {
                              event.stopPropagation();
                              setApplyDialog({ versionId: version.firm_config_version?.id ?? null, fromSave: false });
                            }}
                            disabled={applyActionDisabled}>
                            {DAILY_NAV_APPLY_BUTTON_LABEL}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : undefined
                  }>
                  <ListItemButton
                    selected={version.id === selectedVersionId}
                    data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryItem(version.id)}
                    onClick={() =>
                      setSelectedVersionId(version.id === selectedVersionId ? null : (version.id ?? null))
                    }>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <span>{formatEffectivePeriod(version)}</span>
                          {/* The COMPANY anchor, not the firm one: separate id spaces. */}
                          {version.id === anchoredCompanyVersionId && (
                            <Chip
                              size="small"
                              color={hasDivergence ? 'warning' : 'success'}
                              label={
                                hasDivergence
                                  ? `In effect for ${dailyNavDateLabel(governingNavDate)} NAV`
                                  : 'Current — today’s NAV and the next'
                              }
                              data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryCurrentChip(version.id)}
                            />
                          )}
                          {hasDivergence && version.is_active && version.id !== anchoredCompanyVersionId && (
                            <Chip size="small" color="info" label={dailyNavNextDayLabel(nextNavDate)} />
                          )}
                          {anchoredCompanyVersionId === null && version.is_active && (
                            <Chip
                              size="small"
                              color="success"
                              label="Current"
                              data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryCurrentChip(version.id)}
                            />
                          )}
                        </Stack>
                      }
                      secondary={[
                        version.created_by_name ? `Changed by ${version.created_by_name}` : null,
                        CHANGE_TRIGGER_LABELS[version.change_trigger],
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No configuration changes have been saved yet.</Typography>
          )}
          {hasMoreHistory && (
            <Button
              fullWidth
              size="small"
              onClick={() => loadMoreHistory()}
              disabled={isLoadingMoreHistory}
              data-testid={DAILY_MONITORING_TEST_IDS.versionHistoryLoadMore}>
              {isLoadingMoreHistory ? 'Loading...' : 'Load More'}
            </Button>
          )}
          {selectedVersion && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Values in effect {formatEffectivePeriod(selectedVersion)}
              </Typography>
              {selectedVersion.firm_config_version && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Anchored to the firm config effective {formatEffectivePeriod(selectedVersion.firm_config_version)}
                </Typography>
              )}
              {/* That version's own anchor, so history shows the windows then. */}
              {getDailyNavThresholdGroups(
                selectedVersion.firm_config_version?.short_window_sessions,
                selectedVersion.firm_config_version?.long_window_sessions
              ).map(group => (
                <Box key={group.title} sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {group.title.toUpperCase()}
                  </Typography>
                  {group.fields.map(field => {
                    const overrideValue = selectedVersion[field.key];
                    const firmValue = selectedVersion.firm_config_version?.[field.key];
                    const displayValue = fractionToPercent(overrideValue) || fractionToPercent(firmValue) || '—';

                    return (
                      <Typography key={field.key} variant="body2">
                        {field.label}: {displayValue}%{' '}
                        {overrideValue === null || overrideValue === undefined ? '(inherited)' : '(override)'}
                      </Typography>
                    );
                  })}
                </Box>
              ))}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  COMPS WATCHLIST
                </Typography>
                {selectedVersion.comps?.length ? (
                  <List dense data-testid={DAILY_MONITORING_TEST_IDS.historyCompsList(selectedVersion.id)}>
                    {selectedVersion.comps.map(comp => (
                      <ListItemText
                        key={comp.cap_iq_id}
                        primary={`${comp.ticker_symbol} — ${comp.company_name}`}
                        secondary={COMP_TYPE_LABELS[comp.comp_type]}
                      />
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid={DAILY_MONITORING_TEST_IDS.historyNoCompsHint(selectedVersion.id)}>
                    No comps
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>
      <Dialog
        open={applyDialog !== null}
        onClose={() => setApplyDialog(null)}
        data-testid={DAILY_MONITORING_TEST_IDS.applyConfirmDialog}>
        <DialogTitle>{applyDialogTitle}</DialogTitle>
        <DialogContent>
          <DailyNavConfigDiff
            rows={companyDialogDiff}
            fromLabel="Previous"
            toLabel="Saved"
            testId="daily-monitoring-apply-diff"
          />
          <Typography variant="body2" sx={{ mt: companyDialogDiff.length ? 2 : 0 }}>
            {dailyNavNextDayLabel(nextNavDate)}.
          </Typography>
          {canApplyNow ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Applying now also re-points {companyInfo?.name ?? 'this company'}&apos;s record for{' '}
              {dailyNavApplyDatesLabel(applicableDates)}, the day still in progress. This cannot be undone.
            </Typography>
          ) : (
            // Why no apply is offered, rather than leaving it unexplained.
            applyUnavailableReason && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {applyUnavailableReason}
              </Typography>
            )
          )}
        </DialogContent>
        <DialogActions>
          {canApplyNow ? (
            <>
              {/* Not "Cancel": the config is saved either way. */}
              <Button onClick={() => setApplyDialog(null)}>Next NAV day only</Button>
              <Button
                variant="contained"
                onClick={handleConfirmApply}
                // The handler bails out silently on a null id: disable, do not no-op.
                disabled={isApplyPending || applyFirmVersionId === null}
                data-testid={DAILY_MONITORING_TEST_IDS.applyConfirmButton}>
                Apply now
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setApplyDialog(null)}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export { DailyMonitoring };
